<script setup lang="ts">
import { computed } from 'vue';
import dayjs from 'dayjs';
import { useI18n } from 'vue-i18n';
//@ts-ignore
import Icon from 'next/icon/Icon.vue';
//@ts-ignore
import { useStoreGetters } from 'dashboard/composables/store';

interface ISummary {
  agent: string;
  agentCommitment: string;
  issue: string;
  issueTime: string;
  issueTimeSeconds: number;
  keyInfo: IKeyInfo;
  repeat: boolean;
}

interface IKeyInfo {
  userId?: string;
  orderId?: string;
  reverseId?: string;
  phone?: string;
  goodsId?: string;
  skuId?: string;
}
const { t } = useI18n();

const getters = useStoreGetters();

const currentChat = computed(() => getters.getSelectedChat.value);
const SUMMARY_KEY = 'ub_ai_summary';

const summaryList = computed(() => {
    const summaryValue = currentChat.value.custom_attributes?.[SUMMARY_KEY];
  let res: ISummary[] = [];
  try {
    res = JSON.parse(summaryValue || '{}');
  } catch (e) {
    console.error('Error parsing ub_ai_summary', e);
  }
  return res;
});

const evenClass = [
  '[&>*:nth-child(odd)]:!bg-white [&>*:nth-child(even)]:!bg-slate-25',
  'dark:[&>*:nth-child(odd)]:!bg-slate-900 dark:[&>*:nth-child(even)]:!bg-slate-800/50',
];

const formatDate = (date: string | number) => {
  if (!date) return '-';
  if (typeof date === 'number') {
    return dayjs.unix(date).format('YYYY-MM-DD HH:mm');
  }
  return dayjs(date).format('YYYY-MM-DD HH:mm');
};

const I18N_PATH = 'CONVERSATION_SIDEBAR.CONVERSATION_SUMMARY';
const text = (key: string) => t(`${I18N_PATH}.${key}`);

const KEY_LABLES: Array<[label: string, prop: keyof IKeyInfo]> = [
  ['USER_ID', 'userId'],
  ['ORDER_ID', 'orderId'],
  ['REVERSE_ID', 'reverseId'],
  ['PHONE', 'phone'],
  ['GOODS_ID', 'goodsId'],
  ['SKU_ID', 'skuId'],
];

const getKeyInfo = (keyInfo: IKeyInfo) => {
  return KEY_LABLES.map(([label, prop]) => {
    const value = keyInfo[prop];
    const text = t(`${I18N_PATH}.KEY_INFO.${label}`);
    return value ? `${text}: ${value}` : '';
  })
    .filter(Boolean)
    .join(' | ');
};
</script>

<template>
  <div class="overflow-auto px-4" :class="evenClass">
    <div v-if="summaryList.length === 0">
      <div class="m-0 text-sm text-slate-400 dark:text-slate-700">
        {{ text('NO_SUMMARY') }}
      </div>
    </div>
    <div
      v-else
      v-for="item in summaryList"
      :key="item.issueTimeSeconds || item.issueTime"
      class="conversation-summary py-3"
    >
      <div class="conv-summary-date">{{ formatDate(item.issueTimeSeconds || item.issueTime) }}</div>
      <div class="conv-summary-item">
        <label>{{ text('ISSUE') }}</label>
        <span>{{ item.issue }}</span>
      </div>
      <div class="conv-summary-item flex items-center">
        <label>{{ text('REPEAT') }}</label>
        <span
          class="flex items-center"
          :class="item.repeat ? 'text-n-ruby-11' : 'text-n-slate-10'"
        >
          <span>{{ item.repeat ? text('YES') : text('NO') }}</span>
          <Icon icon="i-lucide-check" class="size-4" v-if="item.repeat" />
          <Icon icon="i-lucide-x" class="size-4" v-else />
        </span>
      </div>
      <div class="conv-summary-item flex items-center">
        <label>{{ text('PROCESSOR') }}</label>
        <span>{{ item.agent }}</span>
      </div>
      <div class="conv-summary-item">
        <label>{{ text('AGENT_COMMITMENT') }}</label>
        <span>{{ item.agentCommitment }}</span>
      </div>
      <div class="conv-summary-item">
        <label>{{ text('KEY_INFO.TITLE') }}</label>
        <span>{{ getKeyInfo(item.keyInfo) }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
.conversation-summary {
  font-size: 12px;

  label {
    font-size: 12px;
    line-height: 1.25rem;
  }

  .conv-summary-date {
    font-weight: bold;
  }

  label::after {
    content: ':';
    margin-right: 4px;
  }
}

.conversation-summary:not(:last-child) {
  border-bottom: 1px solid rgb(240 240 243 / var(--tw-border-opacity));
}
</style>
